export interface PaginationMeta {
  limit: number;
  offset: number;
  total: number;
  has_more: boolean;
}

export type SubscriptionTrackerPagination = PaginationMeta;

// --- User Sub ---

export interface SubscriptionTrackerUser {
  user_id: number;
  username: string;
  email: string;
  team_id: number;
  team_name: string;
  seat_no: number | null;
}

export interface TeamSubscriptionSummary {
  team_id: number;
  wired_sport_codes?: string[];
  wired_sport_count: number;
  active_sub_count: number;
  team_sports: TeamSportSubscriptionDetail[];
}

export interface TeamSubscriptionSummariesResponse {
  team_summaries: Record<string, TeamSubscriptionSummary>;
}

export interface SubscriptionTrackerListResponse {
  users: SubscriptionTrackerUser[];
  pagination: PaginationMeta;
  team_summaries?: Record<string, TeamSubscriptionSummary>;
}

export interface SubscriptionTrackerParams {
  search?: string;
  team_id?: number;
  limit?: number;
  offset?: number;
  /** Default false — load team summaries via team-summaries endpoint for faster first paint. */
  includeTeamSummaries?: boolean;
  trial_status?: 'trial' | 'trial_end';
  trial_end_from?: string;
  trial_end_to?: string;
  /** Team-sport term filters (grace / sub), same values as team tracker. */
  subscription_status?: 'grace' | 'grace_ended' | 'ended' | 'subscribed' | 'locked';
  subscription_end_from?: string;
  subscription_end_to?: string;
  sport_code?: string;
}

export interface SportSubscriptionDetail {
  sport_code: string;
  /** Backend returns trial / trial_end only under user_sports. */
  status: string;
  start_date: string | null;
  end_date: string | null;
  days_remaining: number | null;
  is_grace: boolean | null;
}

export interface UserSubscriptionDetailResponse {
  user_id: number;
  username: string;
  email: string;
  team_id: number;
  team_name: string;
  sub_status: string;
  sub_until: number | null;
  user_sports: SportSubscriptionDetail[];
  team_sports: TeamSportSubscriptionDetail[];
}

// --- Team Sub ---

export interface TeamSubscriptionTrackerItem {
  team_id: number;
  team_name: string;
  team_code: string;
  seats_occupied: number;
  seats_remaining: number;
  seat_limit: number;
}

export interface TeamSubscriptionTrackerListResponse {
  teams: TeamSubscriptionTrackerItem[];
  pagination: PaginationMeta;
}

export interface TeamSubscriptionTrackerParams {
  search?: string;
  limit?: number;
  offset?: number;
  subscription_status?: 'grace' | 'grace_ended' | 'ended' | 'subscribed' | 'locked';
  subscription_start_from?: string;
  subscription_start_to?: string;
  subscription_end_from?: string;
  subscription_end_to?: string;
  sport_code?: string;
}

export interface TeamSportSubscriptionDetail {
  sport_code: string;
  /** UI label: trial | grace | subscribed | grace_ended | subscription_end */
  display_status: string;
  /** Raw DB status (often 'subscribed' even in grace). */
  status: string;
  start_date: string | null;
  end_date: string | null;
  days_remaining: number | null;
  is_grace: boolean | null;
  /** Billing username who activated; null for trial / unknown. */
  activated_by?: string | null;
}

export interface TeamSubscriptionDetailResponse {
  team_id: number;
  team_name: string;
  team_code: string;
  sports: TeamSportSubscriptionDetail[];
}

export interface TeamUserSubscriptionItem {
  user_id: number;
  username: string;
  email: string;
  seat_no: number | null;
  is_active: boolean | null;
  is_admin: boolean;
  team_admin: boolean;
  /** ISO timestamp; null if never logged in */
  last_login: string | null;
  login_count: number;
}

/** Included team seats before overflow (hover only for seat_no > this). */
export const TEAM_SEAT_LIMIT = 5;

export interface TeamUserSubscriptionListResponse {
  team_id: number;
  team_name: string;
  users: TeamUserSubscriptionItem[];
  pagination: PaginationMeta;
}

export interface TeamSubscriptionUsersParams {
  search?: string;
  limit?: number;
  offset?: number;
}

// --- Conference Sub ---

export const CONFERENCE_TRACKER_SPORTS = [
  'MFB',
  'MBB',
  'WBB',
  'MIH',
  'WIH',
  'MSO',
  'WSO',
  'MVB',
  'WVB',
] as const;

export type ConferenceTrackerSportCode = (typeof CONFERENCE_TRACKER_SPORTS)[number];

export interface ConferenceSubscriptionTrackerItem {
  conference_name: string;
  conference_short_name: string;
  teams_total: number;
  teams_did: number;
  teams_not_did: number;
  did_ratio: number;
}

export interface ConferenceSubscriptionTrackerListResponse {
  sport_code: string;
  conferences: ConferenceSubscriptionTrackerItem[];
  pagination: PaginationMeta;
}

export interface ConferenceSubscriptionTrackerParams {
  sport_code: string;
  search?: string;
  limit?: number;
  offset?: number;
}

export interface ConferenceSubscriptionTeamItem {
  team_id: number | null;
  team_name: string;
  team_code: string | null;
  ever_invoiced: boolean;
  is_paid_subscribed: boolean;
  did: boolean;
}

export interface ConferenceSubscriptionTeamsResponse {
  sport_code: string;
  conference_name: string;
  conference_short_name: string;
  teams_total: number;
  teams_did_count: number;
  teams_not_did_count: number;
  teams_did: ConferenceSubscriptionTeamItem[];
  teams_not_did: ConferenceSubscriptionTeamItem[];
}

export interface ConferenceSubscriptionTeamsParams {
  sport_code: string;
  conference_name: string;
}
